import React from 'react';

import { OUTSIDE_ODO_ID, RETAINER_OPTIONS, type RetainerOption } from './retainerOptions';

interface RetainerSelectorProps {
    selectedRetainer: string | null;
    onSelect: (id: string) => void;
    disclaimerAccepted?: boolean;
    onDisclaimerChange?: (accepted: boolean) => void;
    /** 'list' = full-width stacked rows (default); 'grid' = 2-column priced grid + full-width outside-ODO row. */
    layout?: 'list' | 'grid';
    /** Hide the "order outside of ODO" choice — internal surfaces order through ODO only. */
    includeOutsideOdo?: boolean;
    disabled?: boolean;
}

function optionClass(active: boolean, pad: string): string {
    return `flex items-center gap-2.5 cursor-pointer rounded-xl border ${pad} transition-colors ${
        active ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
    }`;
}

const RetainerSelector: React.FC<RetainerSelectorProps> = ({
    selectedRetainer,
    onSelect,
    disclaimerAccepted = false,
    onDisclaimerChange,
    layout = 'list',
    includeOutsideOdo = true,
    disabled = false
}) => {
    const isOutsideOdo = selectedRetainer === OUTSIDE_ODO_ID;

    const renderOption = (option: RetainerOption, useShort: boolean, pad: string) => (
        <label
            key={option.id}
            className={`${optionClass(selectedRetainer === option.id, pad)} ${
                disabled ? 'opacity-60 pointer-events-none' : ''
            }`}
        >
            <input
                type="radio"
                name="retainer"
                checked={selectedRetainer === option.id}
                disabled={disabled}
                onChange={() => {
                    onSelect(option.id);
                    onDisclaimerChange?.(false);
                }}
                className="w-5 h-5 accent-gray-800 flex-shrink-0"
            />
            <span className="text-sm font-medium text-gray-800">
                {useShort ? option.shortLabel : option.label}
                {option.price && ` - ${option.price}`}
            </span>
        </label>
    );

    const priced = RETAINER_OPTIONS.filter((o) => o.id !== OUTSIDE_ODO_ID);
    const outside = RETAINER_OPTIONS.find((o) => o.id === OUTSIDE_ODO_ID)!;

    return (
        <>
            {layout === 'grid' ? (
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        {priced.map((option) => renderOption(option, true, 'px-3 py-3'))}
                    </div>
                    {includeOutsideOdo && renderOption(outside, false, 'px-4 py-3')}
                </div>
            ) : (
                <div className="space-y-3">
                    {(includeOutsideOdo ? RETAINER_OPTIONS : priced).map((option) =>
                        renderOption(option, false, 'px-4 py-3.5')
                    )}
                </div>
            )}

            {isOutsideOdo && (
                <div className="bg-gray-100 p-4 rounded-xl">
                    <p className="text-sm text-gray-900 leading-relaxed">
                        You&apos;ve indicated you will provide your own retainers for this patient.
                        Please note that ODO cannot speak to or guarantee the quality, fit, or
                        long-term stability of retainers not supplied through our service, and our
                        clinical recommendations are based only on retainers we provide.
                    </p>
                    <label className="flex items-center mt-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={disclaimerAccepted}
                            onChange={(e) => onDisclaimerChange?.(e.target.checked)}
                            className="mr-2"
                            style={{ accentColor: '#0A0A0A' }}
                        />
                        <span className="text-sm text-gray-800">
                            I acknowledge the above information.
                        </span>
                    </label>
                </div>
            )}
        </>
    );
};

export default RetainerSelector;
